/* eslint-disable consistent-return */
import { useEffect, useMemo, useState } from 'react';
import { StoryblokEntry } from '../types';
import { isInStoryblokEditor } from '../utils/isInStoryblokEditor';
import { getStoryblokApi } from 'gatsby-source-storyblok';

interface useStoryblokSSGOptions {
  slug: string;
  resolveLinks?: 'url' | 'story' | '0';
}

export function useStoryblokSSG(entry: StoryblokEntry | null, options: useStoryblokSSGOptions) {
  const { slug, resolveLinks = 'url' } = options;

  const buildStory = useMemo(() => {
    if (!entry) return null;

    return {
      ...entry,
      id: entry.internalId,
      content: JSON.parse(entry.content),
    };
  }, [entry]);

  const [activeStory, setActiveStory] = useState(buildStory);

  // if in Storyblok editor
  useEffect(() => {
    if (!isInStoryblokEditor()) return;

    const fetchDraft = async () => {
      const { data: response } = await getStoryblokApi().getStory(slug, {
        version: 'draft',
        resolve_links: resolveLinks,
      });

      setActiveStory(response.story);
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.action === 'input' && event.data?.story) {
        setActiveStory(event.data.story);
      }
    };

    fetchDraft(); // fetch draft content on mount

    window.addEventListener('storyblok:change', fetchDraft); // re-fetch draft and update content on save
    window.addEventListener('message', handleMessage); // update content on input change
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storyblok:change', fetchDraft);
    };
  }, [resolveLinks, slug]);

  const storyToRender = isInStoryblokEditor() ? activeStory : buildStory;

  return { storyToRender };
}
